import { Link } from "react-router-dom";
import ReliabilityBadge from "@/components/feature/ReliabilityBadge";
import ArticleCard from "@/components/feature/ArticleCard";
import { formatHeat, getAllArticles, getHeadline, sortByLatest } from "@/lib/articles";
import { trending } from "@/mocks/trending";

export default function HeroSection() {
  const headline = getHeadline();
  const latest = sortByLatest(getAllArticles().filter((a) => a.slug !== headline.slug)).slice(0, 4);
  const hotTop = trending.slice(0, 5);

  return (
    <section className="relative bg-background-100 paper-grain" aria-label="首屏头条">
      <h1 className="sr-only">独家大瓜 · 最新首发追踪与每日劲爆内幕更新</h1>

      <div className="shell px-4 py-8 md:px-6 md:py-12">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3 lg:gap-8">
          <div className="lg:col-span-2">
            <article className="group relative overflow-hidden rounded-lg">
              <Link to={`/article/${headline.slug}`} className="block cursor-pointer">
                <div className="relative h-[380px] w-full md:h-[500px]">
                  <img
                    src={headline.cover}
                    alt={headline.title}
                    title={`${headline.title} - 独家大瓜`}
                    className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-foreground-950 via-foreground-950/70 to-foreground-950/10" />
                </div>

                <div className="absolute inset-x-0 bottom-0 p-5 md:p-8">
                  <div className="mb-3 flex flex-wrap items-center gap-2">
                    <span className="inline-flex items-center gap-1 rounded-full bg-accent-400 px-2.5 py-0.5 text-[11px] font-label font-black text-foreground-950">
                      <i className="ri-vip-crown-2-fill" aria-hidden="true" />
                      独家首发
                    </span>
                    <ReliabilityBadge value={headline.reliability} />
                    <span className="rounded-full bg-background-50/15 px-2.5 py-0.5 text-[11px] font-label font-bold text-background-50 backdrop-blur-sm">
                      {headline.topicName}
                    </span>
                  </div>

                  <h2 className="max-w-3xl font-heading text-2xl font-black leading-tight text-background-50 md:text-4xl">
                    {headline.title}
                  </h2>
                  <p className="mt-3 max-w-2xl text-sm leading-relaxed text-background-200 line-clamp-2 md:text-[15px]">
                    {headline.summary}
                  </p>

                  <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-background-300 font-label">
                    <span className="inline-flex items-center gap-1.5">
                      <i className="ri-user-3-line" aria-hidden="true" />
                      {headline.author}
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                      <i className="ri-time-line" aria-hidden="true" />
                      {headline.publishedAt} {headline.time}
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                      <i className="ri-fire-fill text-accent-400" aria-hidden="true" />
                      热度 {formatHeat(headline.heat)}
                    </span>
                    <span className="inline-flex items-center gap-1.5 font-bold text-background-50 transition-transform group-hover:translate-x-1">
                      查看完整时间线
                      <i className="ri-arrow-right-line" aria-hidden="true" />
                    </span>
                  </div>
                </div>
              </Link>
            </article>
          </div>

          <aside className="flex flex-col gap-6">
            <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
              <div className="mb-4 flex items-center justify-between">
                <h3 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                  <i className="ri-radar-line text-primary-500" aria-hidden="true" />
                  最新速递
                </h3>
                <Link to="/topic/shoufa" className="text-xs font-label font-bold text-primary-600 hover:text-primary-700 cursor-pointer">
                  查看全部
                </Link>
              </div>
              <ul className="flex flex-col gap-4">
                {latest.map((a) => (
                  <li key={a.slug}>
                    <ArticleCard article={a} layout="text" />
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
              <h3 className="mb-4 flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                <i className="ri-fire-line text-primary-500" aria-hidden="true" />
                今日热榜 TOP 5
              </h3>
              <ol className="flex flex-col gap-3">
                {hotTop.map((item) => (
                  <li key={item.slug}>
                    <Link
                      to={`/article/${item.slug}`}
                      className="group flex cursor-pointer items-start gap-3"
                    >
                      <span
                        className={`mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md font-label text-xs font-black ${
                          item.rank <= 3
                            ? "bg-primary-500 text-background-50"
                            : "bg-background-200 text-foreground-700"
                        }`}
                      >
                        {item.rank}
                      </span>
                      <span className="min-w-0">
                        <span className="block text-sm font-medium leading-snug text-foreground-900 transition-colors group-hover:text-primary-600 line-clamp-2">
                          {item.title}
                        </span>
                        <span className="mt-1 flex items-center gap-2 text-[11px] text-foreground-500">
                          <span>{item.tag}</span>
                          <span className="inline-flex items-center gap-1">
                            <i
                              className={
                                item.change === "up"
                                  ? "ri-arrow-up-line text-primary-500"
                                  : item.change === "down"
                                  ? "ri-arrow-down-line text-foreground-400"
                                  : "ri-subtract-line text-foreground-400"
                              }
                              aria-hidden="true"
                            />
                            {formatHeat(item.heat)}
                          </span>
                        </span>
                      </span>
                    </Link>
                  </li>
                ))}
              </ol>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}